/**
 * Custom Terms Storage Utility
 * 
 * Provides reliable storage for user-defined terms for each numerology number.
 * Uses localStorage with error handling and data validation.
 */

export interface CustomTermsData {
  [number: number]: string[]; // Map of number to array of custom terms
}

const STORAGE_KEY = 'fr00t_custom_terms';
const STORAGE_VERSION = 1;

interface StoredData {
  version: number;
  data: CustomTermsData;
  lastUpdated: string;
}

/**
 * Get all custom terms from storage
 */
export function getCustomTerms(): CustomTermsData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return {};
    }

    const parsed: StoredData = JSON.parse(stored);
    
    // Validate version and migrate if needed
    if (parsed.version !== STORAGE_VERSION) {
      // Future: handle migrations here
      console.warn('Custom terms storage version mismatch, resetting...');
      return {};
    }

    // Validate data structure
    if (!parsed.data || typeof parsed.data !== 'object') {
      return {};
    }

    // Ensure all values are arrays of strings
    const validated: CustomTermsData = {};
    for (const [key, value] of Object.entries(parsed.data)) {
      const num = parseInt(key, 10);
      if (!isNaN(num) && Array.isArray(value)) {
        validated[num] = value.filter(term => typeof term === 'string' && term.trim().length > 0);
      }
    }

    return validated;
  } catch (error) {
    console.error('Failed to load custom terms:', error);
    return {};
  }
}

/**
 * Save custom terms to storage
 */
export function saveCustomTerms(terms: CustomTermsData): boolean {
  // Validate before saving
  const validated: CustomTermsData = {};
  for (const [key, value] of Object.entries(terms)) {
    const num = parseInt(key, 10);
    if (!isNaN(num) && Array.isArray(value)) {
      validated[num] = value
        .filter(term => typeof term === 'string' && term.trim().length > 0)
        .map(term => term.trim().toUpperCase()); // Normalize to uppercase
    }
  }

  const dataToStore: StoredData = {
    version: STORAGE_VERSION,
    data: validated,
    lastUpdated: new Date().toISOString()
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    
    // Dispatch custom event to notify components of the change
    window.dispatchEvent(new Event('customTermsUpdated'));
    
    return true;
  } catch (error) {
    console.error('Failed to save custom terms:', error);
    
    // Handle quota exceeded error
    if (error instanceof DOMException && error.code === 22) {
      console.warn('LocalStorage quota exceeded, attempting cleanup...');
      try {
        // Try to clear old data (but keep user data)
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('fr00t_') && !key.includes('user') && !key.includes('sacred') && key !== STORAGE_KEY) {
            localStorage.removeItem(key);
          }
        });
        // Retry save
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
        
        // Dispatch custom event to notify components of the change
        window.dispatchEvent(new Event('customTermsUpdated'));
        
        return true;
      } catch (retryError) {
        console.error('Failed to save after cleanup:', retryError);
        return false;
      }
    }
    
    return false;
  }
}

/**
 * Get custom terms for a specific number
 */
export function getCustomTermsForNumber(number: number): string[] {
  const allTerms = getCustomTerms();
  return allTerms[number] || [];
}

/**
 * Add a custom term to a number
 */
export function addCustomTerm(number: number, term: string): boolean {
  const allTerms = getCustomTerms();
  const normalizedTerm = term.trim().toUpperCase();
  
  if (!normalizedTerm) {
    return false;
  }

  // Initialize array if it doesn't exist
  if (!allTerms[number]) {
    allTerms[number] = [];
  }

  // Check if term already exists (case-insensitive)
  const exists = allTerms[number].some(
    existing => existing.toUpperCase() === normalizedTerm
  );

  if (exists) {
    return false; // Term already exists
  }

  // Add term
  allTerms[number].push(normalizedTerm);
  return saveCustomTerms(allTerms);
}

/**
 * Remove a custom term from a number
 */
export function removeCustomTerm(number: number, term: string): boolean {
  const allTerms = getCustomTerms();
  
  if (!allTerms[number]) {
    return false;
  }

  const normalizedTerm = term.trim().toUpperCase();
  allTerms[number] = allTerms[number].filter(
    existing => existing.toUpperCase() !== normalizedTerm
  );

  // Remove empty arrays
  if (allTerms[number].length === 0) {
    delete allTerms[number];
  }

  return saveCustomTerms(allTerms);
}

/**
 * Clear all custom terms for a number
 */
export function clearCustomTermsForNumber(number: number): boolean {
  const allTerms = getCustomTerms();
  
  if (!allTerms[number]) {
    return false;
  }

  delete allTerms[number];
  return saveCustomTerms(allTerms);
}

/**
 * Clear all custom terms
 */
export function clearAllCustomTerms(): boolean {
  try {
    localStorage.removeItem(STORAGE_KEY);
    
    // Dispatch custom event to notify components of the change
    window.dispatchEvent(new Event('customTermsUpdated'));
    
    return true;
  } catch (error) {
    console.error('Failed to clear custom terms:', error);
    return false;
  }
}

/**
 * Get combined terms (default + custom) for a number
 * This is a helper to merge custom terms with default archetype terms
 */
export function getCombinedTermsForNumber(
  number: number,
  defaultTerms: string[]
): string[] {
  const customTerms = getCustomTermsForNumber(number);
  
  // Combine and deduplicate (case-insensitive)
  const combined = [...defaultTerms];
  const defaultUpper = defaultTerms.map(t => t.toUpperCase());
  
  customTerms.forEach(customTerm => {
    if (!defaultUpper.includes(customTerm.toUpperCase())) {
      combined.push(customTerm);
    }
  });

  return combined;
}

